import React, { useState } from 'react';

const SOURCE_LABEL = {
    league: 'League',
    season: 'Season',
    team: 'Team',
    player: 'Player',
    subscriber: 'Subscriber',
    previous_player: 'Alumni',
};

export default function AudiencePreviewPanel({
    loading,
    summary,
    included = [],
    excludedEmails = [],
    onToggleExclude,
    noSaveYet = false,
}) {
    const [search, setSearch] = useState('');

    if (noSaveYet) {
        return (
            <div className="border rounded p-3 h-100 d-flex align-items-center justify-content-center text-muted small text-center">
                Enter a campaign name to start building your audience.
            </div>
        );
    }

    if (loading) {
        return (
            <div className="border rounded p-3 h-100 d-flex align-items-center justify-content-center">
                <div className="spinner-border spinner-border-sm text-muted me-2" role="status" />
                <span className="text-muted small">Building recipient list…</span>
            </div>
        );
    }

    const filtered = included.filter((r) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (r.first_name || '').toLowerCase().includes(q) ||
            (r.last_name || '').toLowerCase().includes(q) ||
            (r.email || '').toLowerCase().includes(q)
        );
    });

    const totalIncluded = (summary?.included ?? included.length);
    const totalExcluded = excludedEmails.length;
    const netCount = totalIncluded - totalExcluded;

    return (
        <div className="border rounded d-flex flex-column" style={{ height: '100%', minHeight: 320 }}>
            {/* Header */}
            <div className="px-3 pt-3 pb-2 border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-semibold small">Recipients</span>
                    <span className="badge bg-primary">{netCount} included</span>
                </div>
                {summary && (
                    <div className="d-flex gap-2 flex-wrap" style={{ fontSize: '0.72rem' }}>
                        {summary.skipped > 0 && <span className="text-muted">{summary.skipped} skipped</span>}
                        {summary.unsubscribed > 0 && <span className="text-danger">{summary.unsubscribed} unsubscribed</span>}
                        {summary.duplicates > 0 && <span className="text-muted">{summary.duplicates} duplicates</span>}
                        {summary.manually_excluded > 0 && <span className="text-warning">{summary.manually_excluded} excluded</span>}
                    </div>
                )}
                <input
                    className="form-control form-control-sm mt-2"
                    placeholder="Filter by name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* Recipient list */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
                {included.length === 0 ? (
                    <div className="text-center text-muted small p-3">
                        No recipients yet. Toggle audience segments to add people.
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center text-muted small p-3">No matches for "{search}".</div>
                ) : (
                    <ul className="list-unstyled mb-0">
                        {filtered.map((r) => {
                            const isExcluded = excludedEmails.includes(r.email);
                            const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email;
                            return (
                                <li
                                    key={r.email}
                                    className={`d-flex align-items-center gap-2 px-3 py-2 border-bottom ${isExcluded ? 'bg-light' : ''}`}
                                    style={{ fontSize: '0.82rem' }}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input mt-0 flex-shrink-0"
                                        checked={!isExcluded}
                                        onChange={() => onToggleExclude && onToggleExclude(r.email)}
                                        title={isExcluded ? 'Click to include' : 'Click to exclude'}
                                    />
                                    <div className="flex-grow-1 overflow-hidden">
                                        <span className={isExcluded ? 'text-muted text-decoration-line-through' : ''}>{name}</span>
                                        <span className="text-muted ms-1" style={{ fontSize: '0.72rem' }}>{r.email}</span>
                                    </div>
                                    <span
                                        className="badge text-bg-light border flex-shrink-0"
                                        style={{ fontSize: '0.65rem' }}
                                    >
                                        {SOURCE_LABEL[r.source] || r.source}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
